import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../../shared/api';
import type { User } from '../../types/biApp';

type Locale = 'hr' | 'en';
const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';
const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const btn = (active: boolean): React.CSSProperties => ({ padding: '6px 12px', borderRadius: 999, border: '1px solid ' + (active ? BROWN : '#e0d6c0'), background: active ? BROWN : '#fff', color: active ? '#fff' : BROWN, cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' });
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: BROWN, borderBottom: '2px solid #efe6d2' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: '0.86rem', borderBottom: '1px solid #f3ecdb' };

type Subject = { subject_type: 'edu_subject' | 'circular'; subject_id: number; title: string; ack_mode: 'link' | 'pin'; acks: number };
type Ack = { id: number; name: string; acked_at: string; source: string };

export function AckTab({ token, locale, isSuper }: { token: string; locale: Locale; isSuper: boolean; user: User }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<Subject[] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [who, setWho] = useState<{ s: Subject; list: Ack[] | null } | null>(null);

    const load = useCallback(() => {
        api<{ subjects: Subject[] }>('/hr/ack/subjects', token).then((r) => setRows(r.subjects)).catch((e) => { setErr(String(e?.message ?? e)); setRows([]); });
    }, [token]);
    useEffect(load, [load]);

    const setMode = async (s: Subject, mode: 'link' | 'pin') => {
        try { await mutate('/hr/ack/mode', token, 'PATCH', { subject_type: s.subject_type, subject_id: s.subject_id, mode }); load(); }
        catch (e) { setErr(String((e as Error)?.message ?? e)); }
    };
    const openWho = async (s: Subject) => {
        setWho({ s, list: null });
        try { const r = await api<{ acks: Ack[] }>(`/hr/ack?subject_type=${s.subject_type}&subject_id=${s.subject_id}`, token); setWho({ s, list: r.acks }); }
        catch { setWho({ s, list: [] }); }
    };
    const ackUrl = (s: Subject) => `${window.location.origin}/?ack=${s.subject_type}&id=${s.subject_id}`;

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={card}>
                <strong style={{ color: BROWN }}>{t('Evidencija čitanja — PIN ili link', 'Read tracking — PIN or link')}</strong>
                <div className="muted" style={{ fontSize: '0.78rem', marginTop: 6 }}>
                    {t('„PIN" = dijeljeni link gdje svatko ukuca svoj PIN (više ljudi, jedan ekran). „Link" = jedno čitanje po otvaranju.',
                       '"PIN" = shared link where everyone enters their PIN (many people, one screen). "Link" = single read on open.')}
                    {!isSuper ? ' ' + t('(Način mijenja superadministrator.)', '(Mode changed by superadmin.)') : ''}
                </div>
            </div>

            <div style={card}>
                {err ? <div style={{ color: '#b14040', fontSize: '0.82rem' }}>⚠ {err}</div> : null}
                {rows === null ? <div className="muted">{t('Učitavam…', 'Loading…')}</div> : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr>
                                <th style={th}>{t('Naslov', 'Title')}</th>
                                <th style={th}>{t('Tip', 'Type')}</th>
                                <th style={{ ...th, textAlign: 'right' }}>{t('Potvrde', 'Acks')}</th>
                                <th style={th}>{t('Način', 'Mode')}</th>
                                <th style={th}></th>
                            </tr></thead>
                            <tbody>
                                {rows.map((s) => (
                                    <tr key={s.subject_type + s.subject_id}>
                                        <td style={td}>{s.title}</td>
                                        <td style={td}>{s.subject_type === 'edu_subject' ? t('Edukacija', 'Education') : t('Okružnica', 'Circular')}</td>
                                        <td style={{ ...td, textAlign: 'right' }}>{s.acks || '—'}</td>
                                        <td style={td}>
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <button type="button" disabled={!isSuper} style={btn(s.ack_mode === 'link')} onClick={() => setMode(s, 'link')}>link</button>
                                                <button type="button" disabled={!isSuper} style={btn(s.ack_mode === 'pin')} onClick={() => setMode(s, 'pin')}>PIN</button>
                                            </div>
                                        </td>
                                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            {s.ack_mode === 'pin' ? <a href={ackUrl(s)} target="_blank" rel="noreferrer" style={{ ...btn(false), textDecoration: 'none', marginRight: 6 }}>🔗 {t('Link', 'Link')}</a> : null}
                                            <button type="button" style={btn(false)} onClick={() => openWho(s)}>{t('Tko', 'Who')}</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {who ? (
                <div onClick={() => setWho(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(20,16,8,0.32)', backdropFilter: 'blur(2px)', zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}>
                    <aside onClick={(e) => e.stopPropagation()} style={{ width: 'min(480px, 96vw)', height: '100%', background: 'linear-gradient(180deg,#fffdf6,var(--ad-bg))', boxShadow: '-20px 0 60px rgba(107,79,28,0.22)', overflowY: 'auto', padding: 18 }}>
                        <strong style={{ color: BROWN }}>{t('Potvrdili', 'Acknowledged')} — {who.s.title}</strong>
                        {who.list === null ? <div className="muted" style={{ marginTop: 10 }}>{t('Učitavam…', 'Loading…')}</div>
                            : who.list.length === 0 ? <div className="muted" style={{ marginTop: 10 }}>{t('Još nitko.', 'No one yet.')}</div>
                            : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                                    <thead><tr><th style={th}>{t('Djelatnik', 'Employee')}</th><th style={th}>{t('Vrijeme', 'Time')}</th></tr></thead>
                                    <tbody>
                                        {who.list.map((a) => <tr key={a.id}><td style={td}>{a.name}</td><td style={td}>{new Date(a.acked_at.replace(' ', 'T')).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR')}</td></tr>)}
                                    </tbody>
                                </table>
                            )}
                        <button type="button" style={{ ...btn(false), marginTop: 14 }} onClick={() => setWho(null)}>✕ {t('Zatvori', 'Close')}</button>
                    </aside>
                </div>
            ) : null}
        </div>
    );
}
